import { Button, Carousel, message, Dropdown, Modal, DatePicker, Form } from "antd";
import { useEffect, useState } from "react";
import { getHighlightSections, deleteHighlightSection, updateHighlightSection } from "../../apis/highLightSectionsApi";
import Title from "antd/es/typography/Title";
import { MoreOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import Features from "../../enums/features";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const HighlightSection = () => {
    const [highlightSections, setHighlightSections] = useState([]);
    const [isHighlightCrudEnabled, setIsHighlightCrudEnabled] = useState(false);
    
    // Modal states
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const fetchHighlightSections = async (crudEnabled = isHighlightCrudEnabled) => {
        try {
            const today = new Date();
            // Si tiene CRUD, no enviamos start_date para que pueda ver los del futuro
            const startDateParam = crudEnabled ? null : today;
            const response = await getHighlightSections(startDateParam, today);
            setHighlightSections(response.data);
        } catch (error) {
            message.error("Error al cargar las secciones destacadas");
        }
    }

    useEffect(() => {
        const features = JSON.parse(localStorage.getItem('user_features') || '{}');
        const crudEnabled = features[Features.HIGHLIGHT_CRUD] || false;
        setIsHighlightCrudEnabled(crudEnabled);
        fetchHighlightSections(crudEnabled);
    }, []);

    const handleDelete = (id) => {
        Modal.confirm({
            title: '¿Estás seguro de eliminar esta sección destacada?',
            content: 'Se borrará la imagen y ya no estará disponible.',
            okText: 'Sí, eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    await deleteHighlightSection(id);
                    message.success("Sección destacada eliminada correctamente");
                    fetchHighlightSections();
                } catch (e) {
                    message.error("Error al eliminar la sección");
                }
            }
        });
    };

    const openEditModal = (section) => {
        setEditingSection(section);
        
        const parseDate = (dateStr) => {
            if (!dateStr) return null;
            const d = new Date(dateStr);
            return dayjs(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`);
        };

        form.setFieldsValue({
            dates: [parseDate(section.start_date), parseDate(section.end_date)]
        });
        setIsEditModalVisible(true);
    };

    const closeEditModal = () => {
        setIsEditModalVisible(false);
        setEditingSection(null);
        form.resetFields();
    };

    const handleEditSubmit = async (values) => {
        setLoading(true);
        try {
            const start_date = values.dates[0].format('YYYY-MM-DD');
            const end_date = values.dates[1].format('YYYY-MM-DD');
            await updateHighlightSection(editingSection.id, { start_date, end_date });
            message.success("Sección destacada actualizada correctamente");
            closeEditModal();
            fetchHighlightSections();
        } catch (error) {
            message.error("Error al actualizar la sección");
        } finally {
            setLoading(false);
        }
    };

    if (!highlightSections || highlightSections.length === 0) return null;

    return (
        <>
            <Title level={4} className='text-center'>Destacados</Title>
            <div className="themed-block mx-auto mt-2 mb-5" style={{width: "42.19vh", height: "75vh", overflow: 'visible', position: 'relative'}}>
                <style>{`.highlight-carousel .slick-list, .highlight-carousel .slick-track { overflow: hidden !important; }`}</style>
                <Carousel
                    className="highlight-carousel"
                    arrows={highlightSections.length > 1}
                    dots={false}
                    autoplay
                    >
                    {highlightSections.map((highlightSection, index) => (
                        <div key={index}>
                            <div style={{width: "42.19vh", height: "75vh", position: "relative", overflow: "hidden"}}>
                                {isHighlightCrudEnabled && (
                                    <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1001 }}>
                                        <Dropdown
                                            menu={{
                                                items: [
                                                    {
                                                        key: 'edit',
                                                        icon: <EditOutlined />,
                                                        label: 'Editar Fechas',
                                                        onClick: () => openEditModal(highlightSection)
                                                    },
                                                    {
                                                        key: 'delete',
                                                        icon: <DeleteOutlined />,
                                                        label: 'Eliminar',
                                                        danger: true,
                                                        onClick: () => handleDelete(highlightSection.id)
                                                    }
                                                ]
                                            }}
                                            trigger={['click']}
                                            placement="bottomRight"
                                        >
                                            <Button type="text" shape="circle" icon={<MoreOutlined style={{ fontSize: '20px', color: '#fff', textShadow: '0 0 4px rgba(0,0,0,0.8)' }} />} />
                                        </Dropdown>
                                    </div>
                                )}
                                {highlightSection.type === 'image' ? (
                                    <img
                                    src={`/img/highlightSections/${highlightSection.asset}`}
                                    alt=""
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block"
                                    }}
                                    />
                                ) : highlightSection.type === 'video' ? (
                                    <video
                                    autoPlay
                                    muted
                                    loop
                                    preload="auto"
                                    className="portrait-video landingVideo"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block"
                                    }}
                                    src={`/video/highlightSections/${highlightSection.asset}`}
                                    />
                                ) : null}
                                {highlightSection.link && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            bottom: '10px',
                                            left: 0,
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            paddingBottom: '10px',
                                            zIndex: 1000,
                                        }}
                                    >
                                        <a href={highlightSection.link}>
                                            <Button className="btn themed-btn" style={{lineHeight: 0}}>Ver más</Button>
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </Carousel>
            </div>

            <Modal
                title="Editar Fechas de Destacado"
                open={isEditModalVisible}
                onCancel={closeEditModal}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleEditSubmit}
                >
                    <Form.Item
                        name="dates"
                        label="Rango de Fechas"
                        rules={[{ required: true, message: 'Selecciona las nuevas fechas' }]}
                    >
                        <RangePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Button onClick={closeEditModal}>Cancelar</Button>
                        <Button type="primary" htmlType="submit" loading={loading} className="themed-btn">
                            Guardar
                        </Button>
                    </div>
                </Form>
            </Modal>
        </>
    );
};

export default HighlightSection;
